import * as React from "react";
import { openExternalUrl } from "../../external_link";
import {
  TrainingCenterCaregiverPDFBundleItemWatchSegmentParams,
  TrainingCenterDisplayLanguage,
} from "../../messages/TrainingCenter";
import { STRINGS } from "../../strings";
import { translate } from "../../translate";
import { DocumentOverlayDownloadAction } from "../DocumentOverlayDownloadAction";
import { If } from "../Generic/If";
import { GoogleDocsViewer } from "../GoogleDocsViewer";
import { formatDuration } from "./TrainingCenterBundleItemScreen";
import { generateClientUniqueId, useInterval } from "./TrainingCenterVideoPlayer/Utils";

export interface TrainingCenterPDFMediaItemProps {
  language: TrainingCenterDisplayLanguage;
  pdfUrl: string;
  allowPDFDownload: boolean;
  timeLeft: number | null;
  onPDFLoad: () => void;
  onWatchSegment: (
    clientUniqueId: number,
    segment: TrainingCenterCaregiverPDFBundleItemWatchSegmentParams
  ) => void;
}

const WATCH_SEGMENT_MILLISECONDS_INTERVAL = 5000;

export const TrainingCenterPDFMediaItem = React.memo(function TrainingCenterPDFMediaItem(
  props: TrainingCenterPDFMediaItemProps
): JSX.Element {
  const clientUniqueId = React.useRef<number>(generateClientUniqueId());
  const startedWatching = React.useRef<Date | null>(null);

  // TODO !!! We should also listen for events that put our app in the
  // background/foreground, and pause the timer when we are in the
  // background. (The best way to do this is to create an improved version
  // of "useInterval" that automatically watches for these events and
  // doesn't trigger when in the background)

  const fireWatchSegment = React.useCallback(() => {
    if (startedWatching.current !== null) {
      const secondsWatched = Math.max(
        1,
        Math.floor((new Date().getTime() - startedWatching.current.getTime()) / 1000)
      );

      props.onWatchSegment.call(null, clientUniqueId.current, {
        language: props.language,
        secondsWatched: secondsWatched,
      });
    }
  }, [props.language, props.onWatchSegment]);

  useInterval(() => {
    fireWatchSegment();
  }, WATCH_SEGMENT_MILLISECONDS_INTERVAL);

  React.useEffect(() => {
    clientUniqueId.current = generateClientUniqueId();
    if (startedWatching.current !== null) {
      startedWatching.current = new Date();
    }

    return () => {
      fireWatchSegment();
    };
  }, [fireWatchSegment, props.onWatchSegment]);

  const handlePDFLoad = React.useCallback(() => {
    console.log("PDF Loaded! Watching...");
    startedWatching.current = new Date();
    props.onPDFLoad.call(null);
  }, [props.onPDFLoad]);

  return (
    <TrainingCenterPDFView
      pdfUrl={props.pdfUrl}
      timeLeft={props.timeLeft}
      allowPDFDownload={props.allowPDFDownload}
      onPDFLoad={handlePDFLoad}
    />
  );
});

interface TrainingCenterPDFViewProps {
  pdfUrl: string;
  allowPDFDownload: boolean;
  timeLeft: number | null;
  onPDFLoad: () => void;
}

const TrainingCenterPDFView = React.memo(function TrainingCenterPDFView(
  props: TrainingCenterPDFViewProps
): JSX.Element {
  const handleClickDownload = React.useCallback(() => {
    openExternalUrl(props.pdfUrl);
  }, [props.pdfUrl]);

  return (
    <>
      {props.timeLeft !== null ? (
        <div className="VideoScreen__pdfWatchedSecondsCounter">
          {props.timeLeft <= 0
            ? translate(STRINGS.TEST_REQUIRED)
            : formatDuration(props.timeLeft) + " " + translate(STRINGS.REMAINING)}
        </div>
      ) : null}
      <div className="VideoScreen_mediaOverlayActions">
        <If condition={props.allowPDFDownload}>
          <DocumentOverlayDownloadAction documentType="PDF" onClickDownload={handleClickDownload} />
        </If>
      </div>
      <div className="VideoScreen__documentContainer">
        <GoogleDocsViewer documentUrl={props.pdfUrl} onLoad={props.onPDFLoad} />
      </div>
    </>
  );
});
